/* assets/atd-youtube-shorts.js */
(() => {
    const smoothScrollTo = (el, left) => el.scrollTo({ left, behavior: 'smooth' });

    /* helper to toggle smooth behavior */
    const setSmooth = (vp, on) => {
        // Empty string removes the inline override, letting the CSS rule take effect again
        vp.style.scrollBehavior = on ? '' : 'auto';
    };

    // add near the top (helpers)
    const shouldCenter = (viewport) => {
        // if content width is not wider than viewport, there’s nothing to scroll
        return (viewport.scrollWidth - viewport.clientWidth) <= 1; // 1px tolerance
    };

    const updateCentering = (viewport, track) => {
        track.classList.toggle('is-centered', shouldCenter(viewport));
    };

    const currentIndex = (viewport, items) => {
        const padLeft = parseFloat(getComputedStyle(viewport).paddingInlineStart || 0);
        const x = viewport.scrollLeft + padLeft + 1;
        let idx = 0, min = Infinity;
        items.forEach((it, i) => {
            const d = Math.abs(it.offsetLeft - x);
            if (d < min) { min = d; idx = i; }
        });
        return idx;
    };

    // --- CHANGE: safer, clamped scrollToIndex with explicit handling for index 0 ---
    const scrollToIndex = (viewport, items, i) => {
        if (!viewport || !items || !items.length) return;

        // clamp index to bounds
        const index = Math.max(0, Math.min(items.length - 1, i));

        const padLeft = parseFloat(getComputedStyle(viewport).paddingInlineStart || 0) || 0;

        // For the very first item, the "correct" start is always scrollLeft = 0.
        // This avoids subtle geometry/rounding issues when combining offsetLeft + padding.
        let target = index === 0
            ? 0
            : (items[index].offsetLeft - padLeft);

        // clamp scrollLeft into the valid range [0, maxScrollLeft]
        const maxScrollable = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (!Number.isFinite(target)) target = 0;
        target = Math.max(0, Math.min(maxScrollable, target));

        smoothScrollTo(viewport, target);
    };
    // --- END CHANGE ---

    const parseShortId = (urlOrId) => {
        if (!urlOrId) return '';
        try {
            const u = new URL(urlOrId, location.origin);
            if (u.hostname.includes('youtube.') && u.pathname.startsWith('/shorts/')) {
                return u.pathname.split('/')[2] || '';
            }
            if (u.searchParams.get('v')) return u.searchParams.get('v');
        } catch { }
        return urlOrId;
    };

    const makeViewportDraggable = (viewport) => {
        let isDown = false, startX = 0, startLeft = 0;

        const down = (e) => {
            // Touch/stylus: let the browser handle native scroll (mirrors social-media behaviour)
            if (e.pointerType === 'touch') return;
            isDown = true;
            startX = e.pageX;
            startLeft = viewport.scrollLeft;
            viewport.classList.add('is-dragging');
            setSmooth(viewport, false);
            // Capture the pointer so move/up fire on this element even outside the viewport
            viewport.setPointerCapture(e.pointerId);
        };

        const move = (e) => {
            if (!isDown) return;
            viewport.scrollLeft = startLeft - (e.pageX - startX);
        };

        const up = () => {
            if (!isDown) return;
            isDown = false;
            viewport.classList.remove('is-dragging');
            setSmooth(viewport, true);
        };

        viewport.addEventListener('pointerdown', down);
        viewport.addEventListener('pointermove', move);
        // pointerup/pointercancel are captured by the element via setPointerCapture — no global listeners needed
        viewport.addEventListener('pointerup', up);
        viewport.addEventListener('pointercancel', up);
    };

    const wireCustomScrollbar = (viewport, track, thumb) => {
        const minThumbPx = parseFloat(getComputedStyle(thumb).minWidth || 0) || 40;
        const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

        const updateThumb = () => {
            const scrollW = viewport.scrollWidth;
            const viewW = viewport.clientWidth;
            const maxScrollable = Math.max(1, scrollW - viewW);
            const trackW = track.clientWidth;

            const w = clamp((viewW / scrollW) * trackW, minThumbPx, trackW);
            const x = clamp(
                (viewport.scrollLeft / maxScrollable) * (trackW - w),
                0,
                trackW - w
            );
            thumb.style.width = `${w}px`;
            thumb.style.left = `${x}px`;
        };

        viewport.addEventListener('scroll', updateThumb, { passive: true });
        new ResizeObserver(updateThumb).observe(viewport);
        new ResizeObserver(updateThumb).observe(track);
        updateThumb();

        // Click track -> proportional jump
        track.addEventListener('pointerdown', (e) => {
            if (e.target === thumb) return;
            const rect = track.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const trackW = rect.width;
            const thumbW = thumb.offsetWidth;
            const ratio = clamp((clickX - thumbW / 2) / (trackW - thumbW), 0, 1);
            const maxScrollable = viewport.scrollWidth - viewport.clientWidth;
            smoothScrollTo(viewport, ratio * maxScrollable);
        });

        // Drag thumb -> scroll
        let dragging = false, startX = 0, startLeft = 0, trackW = 0, thumbW = 0, maxScrollable = 0;
        const onThumbDown = (e) => {
            dragging = true;
            const evtX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
            startX = evtX;
            startLeft = parseFloat(getComputedStyle(thumb).left) || 0;
            trackW = track.clientWidth;
            thumbW = thumb.offsetWidth;
            maxScrollable = viewport.scrollWidth - viewport.clientWidth;
            thumb.setPointerCapture?.(e.pointerId || 0);
            e.preventDefault?.();
        };
        const onThumbMove = (e) => {
            if (!dragging) return;
            const evtX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
            const dx = evtX - startX;
            const newLeft = Math.max(0, Math.min(trackW - thumbW, startLeft + dx));
            const ratio = (newLeft) / (trackW - thumbW || 1);
            viewport.scrollLeft = ratio * maxScrollable;
        };
        const onThumbUp = () => { dragging = false; };

        thumb.addEventListener('pointerdown', onThumbDown);
        window.addEventListener('pointermove', onThumbMove);
        window.addEventListener('pointerup', onThumbUp);
        thumb.addEventListener('touchstart', onThumbDown, { passive: false });
        window.addEventListener('touchmove', onThumbMove, { passive: false });
        window.addEventListener('touchend', onThumbUp);
    };

    /** Open the Short on YouTube (no modal) */
    const bindCardClicks = (track) => {
        track.addEventListener('click', (ev) => {
            const card = ev.target.closest('.atd-ys-card');
            if (!card) return;
            const url = card.getAttribute('data-href') || '';
            if (url) window.open(url, '_blank', 'noopener');
        });
    };

    /** Enable/disable arrows based on position */
    const updateButtons = (viewport, items, prevBtn, nextBtn) => {
        if (!prevBtn && !nextBtn) return;

        const epsilon = 2; // px tolerance
        const atStart = viewport.scrollLeft <= epsilon;
        const atEnd =
            viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - epsilon;

        if (prevBtn) prevBtn.classList.toggle('is-disabled', atStart);
        if (nextBtn) nextBtn.classList.toggle('is-disabled', atEnd);
    };

    /** Prev/Next click behavior (snap one card) */
    const wirePrevNext = (viewport, items, prevBtn, nextBtn) => {
        prevBtn?.addEventListener('click', () => {
            const i = currentIndex(viewport, items);
            scrollToIndex(viewport, items, Math.max(0, i - 1));
        });
        nextBtn?.addEventListener('click', () => {
            const i = currentIndex(viewport, items);
            scrollToIndex(viewport, items, Math.min(items.length - 1, i + 1));
        });
    };

    const init = () => {
        document.querySelectorAll('[data-carousel]').forEach(root => {
            const viewport = root.querySelector('[data-viewport]');
            const trackUL = root.querySelector('[data-track]');
            if (!viewport || !trackUL) return;

            const items = Array.from(trackUL.children);
            const prevBtn = root.querySelector('[data-prev]');
            const nextBtn = root.querySelector('[data-next]');

            wirePrevNext(viewport, items, prevBtn, nextBtn);

            const barTrack = root.querySelector('[data-scrollbar-track]');
            const barThumb = root.querySelector('[data-scrollbar-thumb]');
            if (barTrack && barThumb) wireCustomScrollbar(viewport, barTrack, barThumb);

            makeViewportDraggable(viewport);
            bindCardClicks(trackUL);

            // one function to handle layout changes
            const onLayoutChange = () => {
                updateButtons(viewport, items, prevBtn, nextBtn);
                updateCentering(viewport, trackUL); // <-- runs on resize
            };

            // scroll only affects buttons
            viewport.addEventListener('scroll', () => updateButtons(viewport, items, prevBtn, nextBtn), { passive: true });

            // sizes/resize: center when not scrollable
            const ro = new ResizeObserver(onLayoutChange);
            ro.observe(viewport);
            ro.observe(trackUL);

            // (optional) also react to individual slide size changes
            items.forEach(it => ro.observe(it));

            // initial
            onLayoutChange();
        });
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
